import * as React from 'react';
import { StyledRadiobox, IStyledRadioboxProps } from './Radiobox.style';
import { useRadioGroupContext } from '../context';

export interface IRadioboxProps extends IStyledRadioboxProps {
  /**
   * string value for radio inputs name to bind them
   * in the same radio group. holds context passed
   * from RadioGroup
   */
  name?: string;

  /**
   * Unique identifier for this input. Required for screen readers
   * to associate this component's with the label that describes it.
   */
  id: string;

  /**
   * value to apply to mark Radiobox disabled.
   * can be a child from RadioGroup
   */
  disabled?: boolean;
  /**
   * string value for radio inputs class
   */
  className?: string;
  /**
   * value of the radiobox to send with data.
   */
  value: string;
  /**
   * On change handler passed to input
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * Determines if radio button is checked by default
   *
   * @type {boolean}
   * @memberof IRadioboxProps
   */
  defaultChecked?: boolean;
}

/**
 * Singular radio button.
 */
const Radiobox = React.forwardRef<HTMLInputElement, IRadioboxProps>(
  (
    { children, name, disabled, onChange, value, defaultChecked, ...props },
    ref
  ) => {
    const { groupId } = useRadioGroupContext();
    const { groupDisabled, groupOnChange } = useRadioGroupContext();
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [_value, setValue] = React.useState('');
    const inputId = `${groupId}`;
    const isDisabled = disabled || groupDisabled;
    const hasValue = value || _value;

    const changeHandler: React.ChangeEventHandler<HTMLInputElement> = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (typeof groupOnChange !== 'undefined') {
        groupOnChange(event);
      }
      if (typeof onChange !== 'undefined') {
        onChange(event);
      } else {
        setValue(event.currentTarget.value);
      }
    };

    return (
      <StyledRadiobox
        {...props}
        name={inputId}
        ref={ref}
        disabled={isDisabled}
        onChange={changeHandler}
        value={hasValue}
        defaultChecked={defaultChecked || false}
      >
        {children}
      </StyledRadiobox>
    );
  }
);

Radiobox.displayName = 'Form.Radiobox';

export default Radiobox;
